import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { API_BASE_URL } from '../../api/config';
import { createFileLogger } from '../../utils/logger';
import { traceService } from '../../services/debug/traceService';

const log = createFileLogger('RETRIEVE_PDF_METADATA');

export const retrievePdfMetadataCommand: SelfContainedCommand = {
  id: 'retrieve_pdf_metadata',
  description: `Retrieve scholarly metadata for an existing PDF entity (Zotero-style citation extraction)

**What it does:**
- Analyzes already-extracted text from PDF entity
- Extracts DOI and looks up via Crossref/OpenAlex APIs
- Returns structured bibliographic metadata (title, authors, journal, DOI, year, etc.)
- Updates PDF entity with retrieved metadata in skeleton.referenceMetadata for future reference

**Use when:**
- User has a PDF and wants citation information
- Building bibliography or reference list
- Need to identify an unknown academic paper
- Want to add proper metadata to a PDF in their library

**🚨 HARD REQUIREMENTS:**
• pdf_id must be a valid PDF entity ID (e.g., "file_small_cat_cherry")
• PDF must have enrichedData with extracted text
• Entity must be of type "File.Document.PDF"`,
  
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  version: '1.0',
  
  schema: {
    parameters: [
      {
        name: 'pdf_id',
        type: 'string',
        description: 'Entity ID of the PDF (e.g., file_small_cat_cherry)',
        required: true,
        examples: ['file_small_cat_cherry', 'file_eager_happy_coral'],
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'pdf_id cannot be empty';
          }
          if (!value.startsWith('file_')) {
            return 'pdf_id must start with "file_" (PDF entity ID)';
          }
          return null;
        }
      }
    ],
    contextRequirements: {
      requiresActiveWindow: false,
      requiresSelectedText: false,
      requiresEntity: false
    }
  },
  
  examples: {
    good: [
      {
        input: {
          pdf_id: 'file_small_cat_cherry'
        },
        expectedOutput: createSuccessResult(
          'Retrieved metadata: Low level alcohol consumption is associated with lower regional brain volume...',
          {
            title: 'Low level alcohol consumption is associated with lower regional brain volume and thickness...',
            authors: [
              { lastName: 'Durazzo', firstName: 'Timothy C.', fullName: 'Durazzo, Timothy C.' },
              { lastName: 'Joseff', firstName: 'Brian D.P.', fullName: 'Joseff, Brian D.P.' }
            ],
            publication: 'Journal of Neuroscience Research',
            year: 2025,
            DOI: '10.1016/S0741832925001260',
            confidence: 0.95,
            source: 'crossref'
          }
        ),
        description: 'Retrieve metadata from PDF entity with DOI (best case)'
      },
      {
        input: {
          pdf_id: 'file_eager_happy_coral'
        },
        expectedOutput: createSuccessResult(
          'Retrieved metadata: Machine Learning in Healthcare',
          {
            title: 'Machine Learning in Healthcare: A Survey',
            authors: [
              { lastName: 'Smith', firstName: 'John', fullName: 'Smith, John' }
            ],
            year: 2024,
            confidence: 0.85,
            source: 'openalex'
          }
        ),
        description: 'Retrieve metadata from PDF without DOI (title search fallback)'
      }
    ],
    bad: [
      {
        input: {},
        error: 'pdf_id cannot be empty',
        why: 'No PDF identifier provided',
        fix: 'Provide pdf_id parameter with valid entity ID'
      },
      {
        input: { pdf_id: 'doc_invalid_type' },
        error: 'Entity is not a PDF',
        why: 'Entity type must be File.Document.PDF',
        fix: 'Provide ID of a PDF entity (starts with file_)'
      },
      {
        input: { pdf_id: 'file_nonexistent' },
        error: 'PDF entity not found',
        why: 'Entity does not exist or user lacks permission',
        fix: 'Check that entity ID is correct and accessible'
      }
    ]
  },
  
  performance: {
    expectedDuration: 3000,  // 3 seconds (external API calls)
    complexity: 'medium',
    memoryIntensive: false
  },
  
  // Custom view data formatter for rich display
  formatViewData: (result: StandardToolResult, toolInput: any) => {
    if (result.status === 'success' && result.data) {
      const metadata = result.data.metadata || result.data;
      
      return {
        type: 'pdf_metadata',
        summary: `Retrieved: ${metadata.title || 'Unknown'}`,
        data: {
          metadata: metadata,
          pdf_id: toolInput.pdf_id,
          entity_updated: result.data.entity_updated,
          timestamp: Date.now()
        },
        meta: {
          confidence: metadata.confidence,
          source: metadata.source,
          authorsCount: metadata.authors?.length || 0
        }
      };
    }
    return null;
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { pdf_id } = params;
    
    // Trace command start
    traceService.log({
      traceId: context.executionId,
      type: 'ai_command_start',
      data: {
        commandId: 'retrieve_pdf_metadata',
        pdf_id
      }
    });
    
    log.log('[retrieve_pdf_metadata] Starting:', { pdf_id });
    
    try {
      // Get auth token
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        return createErrorResult(
          'Authentication required',
          'No auth token found in localStorage',
          'permission'
        );
      }
      
      // Show progress
      if (context.onProgress) {
        context.onProgress({
          type: 'streaming_update',
          toolUseId: context.executionId,
          activity: 'Analyzing PDF and looking up metadata...'
        });
      }
      
      // Call backend API
      const response = await fetch(`${API_BASE_URL}/pdf-metadata/retrieve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        credentials: 'include',
        body: JSON.stringify({ pdf_id })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle specific error cases
        if (response.status === 404) {
          return createErrorResult(
            'PDF not found',
            errorData.detail || 'Could not find the specified PDF entity',
            'validation'
          );
        }
        
        if (response.status === 400) {
          return createErrorResult(
            'Invalid PDF or insufficient text',
            errorData.detail || 'PDF does not have enough extracted text for metadata retrieval',
            'validation'
          );
        }
        
        return createErrorResult(
          'Failed to retrieve metadata',
          errorData.detail || 'Unknown error',
          'execution',
          { 
            metadata: { 
              executionTime: Date.now() - startTime,
              method: 'api_call'
            },
            error: {
              code: 'API_ERROR',
              category: 'execution',
              details: { statusCode: response.status, ...errorData },
              retryable: true
            }
          }
        );
      }
      
      const result = await response.json();
      const metadata = result.metadata;
      
      log.log('[retrieve_pdf_metadata] ✅ Success:', {
        title: metadata.title,
        authors_count: metadata.authors?.length,
        has_doi: !!metadata.DOI,
        confidence: metadata.confidence,
        source: metadata.source,
        entity_updated: result.entity_updated
      });
      
      // Trace successful completion
      traceService.log({
        traceId: context.executionId,
        type: 'ai_command_complete',
        data: {
          commandId: 'retrieve_pdf_metadata',
          success: true,
          has_doi: !!metadata.DOI,
          confidence: metadata.confidence,
          source: metadata.source,
          entity_updated: result.entity_updated
        },
        metadata: { executionTime: Date.now() - startTime }
      });
      
      // Format author string for display
      const authorsStr = metadata.authors && metadata.authors.length > 0
        ? metadata.authors
            .slice(0, 3)
            .map((a: any) => a.fullName || `${a.lastName}, ${a.firstName}`)
            .join('; ') + (metadata.authors.length > 3 ? ` et al. (${metadata.authors.length} authors)` : '')
        : 'Unknown authors';
      
      // Build citation string
      const citation = buildCitationString(metadata);
      
      // Build success message
      const titlePreview = metadata.title.length > 80 
        ? metadata.title.substring(0, 80) + '...'
        : metadata.title;
      
      const successMessage = result.entity_updated
        ? `Retrieved and saved metadata: ${titlePreview}`
        : `Retrieved metadata: ${titlePreview} (entity update failed)`;
      
      return createSuccessResult(
        successMessage,
        {
          ...result,
          citation,
          authors_display: authorsStr,
          // Custom retrieval metadata (not part of StandardToolResult.metadata)
          retrievalInfo: {
            confidence: metadata.confidence,
            source: metadata.source,
            entityUpdated: result.entity_updated
          },
          metadata: {
            ...metadata,
            // Ensure authors are properly formatted for display
            authors: metadata.authors?.map((a: any) => ({
              firstName: a.firstName,
              lastName: a.lastName,
              fullName: a.fullName || `${a.lastName}, ${a.firstName}`
            }))
          }
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'pdf_metadata_retrieval'
          }
        }
      );
      
    } catch (error: any) {
      log.error('[retrieve_pdf_metadata] ❌ Failed:', error);
      
      // Trace error
      traceService.log({
        traceId: context.executionId,
        type: 'ai_command_error',
        data: {
          commandId: 'retrieve_pdf_metadata',
          success: false,
          error: error instanceof Error ? error.message : String(error)
        },
        metadata: { executionTime: Date.now() - startTime }
      });
      
      return createErrorResult(
        'Failed to retrieve PDF metadata',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

/**
 * Build a formatted citation string from metadata (APA-ish style)
 */
function buildCitationString(metadata: any): string {
  const parts: string[] = [];
  
  // Authors
  if (metadata.authors && metadata.authors.length > 0) {
    const authorNames = metadata.authors.map((a: any) => 
      a.fullName || `${a.lastName}, ${a.firstName}`
    );
    
    if (authorNames.length === 1) {
      parts.push(authorNames[0]);
    } else if (authorNames.length === 2) {
      parts.push(`${authorNames[0]} & ${authorNames[1]}`);
    } else if (authorNames.length <= 7) {
      parts.push(authorNames.join(', '));
    } else {
      // More than 7 authors: show first 6, then "...", then last
      const first6 = authorNames.slice(0, 6).join(', ');
      const last = authorNames[authorNames.length - 1];
      parts.push(`${first6}, ... ${last}`);
    }
  }
  
  // Year
  if (metadata.date) {
    // Extract year from date string
    const year = metadata.date.substring(0, 4);
    parts.push(`(${year})`);
  }
  
  // Title
  if (metadata.title) {
    parts.push(metadata.title);
  }
  
  // Journal
  if (metadata.publication) {
    parts.push(`*${metadata.publication}*`);
  }
  
  // Volume/Issue
  if (metadata.volume) {
    const volStr = metadata.issue
      ? `*${metadata.volume}*(${metadata.issue})`
      : `*${metadata.volume}*`;
    parts.push(volStr);
  }
  
  // Pages
  if (metadata.pages) {
    parts.push(metadata.pages);
  }
  
  // DOI
  if (metadata.DOI) {
    parts.push(`https://doi.org/${metadata.DOI}`);
  }
  
  return parts.join('. ') + '.';
}

